/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 화일명 : ScheduleAlert.tsx
// 설명 : 일정관리(X3001M1) 알림 - mainFrame에 항상 떠있으면서 1분마다 "내가 작성한 알림설정 일정 후보"를
//        받아(apiX3001M1Alert), 알림시각(시작 - N분)이 지났고 아직 끝나지 않은 일정을 화면 우하단 토스트로
//        알려줌. 서버가 반복 일정의 개별 발생일을 펼치지 않으므로 여기서 rrule로 "지금 해당하는 발생일"만
//        계산하고, 이미 알린 발생일은 localStorage에 기록해 다시 울리지 않음(사용자가 닫기 전까지 화면엔 유지).
//        - 시각은 DB의 "벽시계 시각"(타임존 없음) 그대로 비교(rrule은 UTC로만 계산하므로 벽시계를 UTC로 취급해
//          계산한 뒤 같은 방식으로 비교).
// 개발이력 : 2026.09.20 최초개발
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { RRule } from 'rrule';
import { API_BASE_URL } from '../config'; // API 기본 URL
import '../styles/schedule.css'; // 일정관리/알림 전용 스타일

interface ScheduleAlertProps {
  enabled: boolean;      // 사용자 메뉴에 일정관리(LINK_KEY='SCHEDULE')가 있을 때만 동작
  onOpenTab: () => void; // 알림에서 "일정관리 열기" 클릭 시
}
interface AlertToast { key: string; title: string; timeText: string }

const POLL_MS = 60 * 1000;
const STORE_KEY = 'schedule_alerted';
const LEGACY_STORE_KEY = 'X3001M1_alerted'; // 이전 키 - 읽기만 하고 다음 저장 때 새 키로 옮김
const KEEP_MS = 7 * 86400000;
const FREQ: Record<string, number> = { D: RRule.DAILY, W: RRule.WEEKLY, M: RRule.MONTHLY, Y: RRule.YEARLY };

// 'YYYY-MM-DD[ T]HH:mm:ss'(벽시계) -> 같은 숫자를 UTC로 취급한 Date
const wallToFake = (s: string): Date => {
  const m = s.match(/(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}):(\d{2}))?/);
  if (!m) return new Date(NaN);
  return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0)));
};
const nowFake = (): number => {
  const n = new Date();
  return Date.UTC(n.getFullYear(), n.getMonth(), n.getDate(), n.getHours(), n.getMinutes(), n.getSeconds());
};
const p2 = (n: number) => String(n).padStart(2, '0');
const fmtFake = (d: Date, withTime: boolean) =>
  `${d.getUTCMonth() + 1}/${d.getUTCDate()}${withTime ? ` ${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}` : ''}`;

const readStore = (): Record<string, number> => {
  try { return { ...JSON.parse(localStorage.getItem(LEGACY_STORE_KEY) || '{}'), ...JSON.parse(localStorage.getItem(STORE_KEY) || '{}') }; } catch { return {}; }
};
const writeStore = (m: Record<string, number>) => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(m)); localStorage.removeItem(LEGACY_STORE_KEY); } catch { /* 저장소 접근 실패 시 무시(중복 알림 가능) */ }
};

const ScheduleAlert: React.FC<ScheduleAlertProps> = ({ enabled, onOpenTab }) => {
  const [toasts, setToasts] = useState<AlertToast[]>([]);

  const check = useCallback(async () => {
    try {
      const res = await axios.post(`${API_BASE_URL}/za/apiX3001M1Alert`, {});
      const rows: any[] = Array.isArray(res.data?.rsResults) ? res.data.rsResults : [];
      const now = nowFake();
      const store = readStore();
      const fresh: AlertToast[] = [];

      rows.forEach(r => {
        const allDay = r.all_day_yn === 'Y';
        const start = wallToFake(r.start_dt);
        const end = wallToFake(r.end_dt);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;
        const durationMs = allDay ? end.getTime() - start.getTime() + 86400000 : end.getTime() - start.getTime();
        const alertMs = Number(r.alert_min) * 60000;
        // 알림시각 <= 지금 < 발생종료  <=>  발생시작 ∈ (지금 - 지속시간, 지금 + 알림선행시간]
        const lo = new Date(now - durationMs), hi = new Date(now + alertMs);
        let occs: Date[];
        if (r.repeat_tp === 'N') {
          occs = start.getTime() > lo.getTime() && start.getTime() <= hi.getTime() ? [start] : [];
        } else {
          const rule = new RRule({
            freq: FREQ[r.repeat_tp], interval: Number(r.repeat_interval) || 1, dtstart: start,
            until: r.repeat_until_dt ? wallToFake(`${r.repeat_until_dt} 23:59:59`) : null,
          });
          occs = rule.between(lo, hi, true);
        }
        occs.forEach(o => {
          const key = `${r.sch_no}|${o.getTime()}`;
          if (store[key]) return;
          store[key] = Date.now();
          const oEnd = new Date(o.getTime() + durationMs);
          const timeText = allDay
            ? `${fmtFake(o, false)} (종일)`
            : `${fmtFake(o, true)} ~ ${p2(oEnd.getUTCHours())}:${p2(oEnd.getUTCMinutes())}`;
          fresh.push({ key, title: r.title, timeText });
        });
      });

      // 오래된 기록 정리
      Object.keys(store).forEach(k => { if (Date.now() - store[k] > KEEP_MS) delete store[k]; });
      writeStore(store);
      if (fresh.length > 0) setToasts(prev => [...prev, ...fresh]);
    } catch { /* 알림은 부가기능 - 조회 실패는 조용히 무시하고 다음 주기에 재시도 */ }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    check();
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') check(); }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [enabled, check]);

  if (!enabled || toasts.length === 0) return null;
  return (
    <div className="sch-alerts">
      {toasts.map(t => (
        <div className="sch-alert" key={t.key}>
          <div className="sch-alert-head">
            <i className="fas fa-bell" /> 일정 알림
            <span className="spacer" />
            <button type="button" aria-label="닫기" onClick={() => setToasts(prev => prev.filter(x => x.key !== t.key))}>×</button>
          </div>
          <div className="sch-alert-title">{t.title}</div>
          <div className="sch-alert-time">{t.timeText}</div>
          <button type="button" className="sch-alert-open" onClick={() => { onOpenTab(); setToasts(prev => prev.filter(x => x.key !== t.key)); }}>일정관리 열기</button>
        </div>
      ))}
    </div>
  );
};

export default ScheduleAlert;
